import type { CrucibleSessionConnection, FallenChoice, Vector2 } from '@sorrem/shared';
import type { CleanRenderResult, UIItemSlotRef } from '@sorrem-client/types';
import type { GameStateBridge, GameUiInput } from '@sorrem-client/ui/shared/game-state-bridge';
import { getStoredSelectedCharacterId } from '@sorrem-client/ui/hooks/use-selected-character-id';
import { getPageAudio } from '@sorrem-client/ui/audio/page-audio';
import { getPageInput } from '@sorrem-client/ui/input/page-input';
import {
    START_CRAFT_UNAVAILABLE,
    type CrucibleGatheringCommand,
    type GameCommand,
    type GameCursorMode,
    type GameMessage,
    type GameStartOptions,
    type StartCraftResult,
} from './protocol';
import type { GatheringEntranceSpot } from './render-config';
import { createPendingCalls } from './pending-calls';
import { assertNever } from '@sorrem/shared';

/** How a game connection begins: straight into a run, or with a crucible's gathering, the run to follow. */
export type GameBegin =
    | { readonly kind: 'run'; readonly options: GameStartOptions }
    | { readonly kind: 'gathering'; readonly withGathering: boolean };

/** A game, wherever it runs: something to start, send commands to and shut down. */
export interface GameConnection {
    /** Hands the canvas over and starts drawing. Called once. */
    begin(start: GameBegin): void;
    /** After a `gathering` begin: starts the run on the scene already drawing. */
    startRun(options: GameStartOptions): void;
    sendGathering(command: CrucibleGatheringCommand): void;
    send(command: GameCommand): void;
    /** A developer snapshot (#303): the live scene drawn alone at `scale` times the canvas size. */
    captureCleanRender(scale: number): Promise<CleanRenderResult>;
    /** Shuts the game down; resolves once it has stopped (its socket has left). */
    dispose(): Promise<void>;
}

/** What the crucible scene reports to the page before a run takes it over. */
export type GameSceneMessage =
    | { readonly type: 'scene-error'; readonly message: string }
    /** A gathering preload of the run's models finished. */
    | { readonly type: 'run-models-ready' }
    /** The gathering set is built for its crucible, so it has something to show; `entrance` places and sizes the entry countdown (see render-config.ts). */
    | { readonly type: 'gathering-ready'; readonly entrance: GatheringEntranceSpot };

/** Messages from the game, plus the scene's own before a run starts. */
export type GameConnectionMessage = GameMessage | GameSceneMessage;

/**
 * Connects a game that will draw into `canvas` (sized before `begin`)
 * and returns the connection to it. Its messages go to `onMessage`.
 */
export type GameConnector = (
    canvas: HTMLCanvasElement,
    onMessage: (message: GameConnectionMessage) => void,
) => GameConnection;

/** What a run needs from the page's session to start. */
export interface GameRunSession {
    readonly sessionConnection: CrucibleSessionConnection;
    /** The account's API session JWT, which authenticates the gameplay socket. */
    readonly sessionToken: string;
}

/**
 * The gameplay actions a React surface can invoke against the running game,
 * independent of the `GameStateBridge` used for reading state back out.
 */
export interface GameSessionApi {
    requestInventoryMove: (requestId: string, itemId: string, target: UIItemSlotRef) => Promise<boolean>;
    requestConsumeCrystal: (itemId: string) => void;
    requestDropItem: (itemId: string) => void;
    requestCancelCrystalUse: () => void;
    requestCastAbility: (skillId: string) => void;
    requestCancelCastAbility: () => void;
    requestLeaveRun: () => void;
    requestFallenChoice: (choice: FallenChoice) => void;
    requestTourHold: (active: boolean) => void;
    requestStartCraft: (slotIndex: number, recipeId: string) => Promise<StartCraftResult>;
    requestCancelCraft: (coalescenceId: number) => void;
}

export interface GamePageHost {
    readonly gameplayApi: GameSessionApi;
    /**
     * Starts drawing the crucible's gathering, with the run to follow on the
     * same scene. Without `withGathering` (a solo direct-entry run) the scene
     * only loads the run's models until it starts. Call at most once, before `startRun`.
     */
    beginGathering(withGathering: boolean): void;
    /** Sends a gathering scene command (roster, join, leave). */
    sendGathering(command: CrucibleGatheringCommand): void;
    /** Starts the run: on the gathering's scene when there is one, else on a fresh one. Takes over input and focus. */
    startRun(session: GameRunSession): void;
    /** Whether `startRun` has been called. */
    readonly isRunStarted: boolean;
    /** Re-measures the canvas and resizes the game's drawing buffer to match. */
    syncCanvasSize(): void;
    /** Gives the game canvas keyboard focus, so gameplay bindings apply. */
    focus(): void;
    /** A developer snapshot (#303): the scene, gathering or run, drawn alone at `scale` times the canvas size. */
    captureCleanRender(scale: number): Promise<CleanRenderResult>;
    /** Shuts the game down; resolves once it has stopped (its socket has left). */
    dispose(): Promise<void>;
}

/** The canvas cursor per mode, from the shared cursor tokens (ui/styles/cursors.scss). */
const CURSOR_CSS: Record<GameCursorMode, string> = {
    default: 'var(--cursor-default)',
    attack: 'var(--cursor-attack)',
    interact: 'var(--cursor-interact)',
};


function measureCanvas(canvas: HTMLCanvasElement): { width: number; height: number } {
    const bounds = canvas.getBoundingClientRect();
    return {
        width: Math.max(1, Math.floor(bounds.width || window.innerWidth)),
        height: Math.max(1, Math.floor(bounds.height || window.innerHeight)),
    };
}

function toGameCommand(input: GameUiInput): GameCommand {
    switch (input.kind) {
        case 'display-settings':
            return { type: 'set-display-settings', settings: input.settings };
        case 'reduced-motion':
            return { type: 'set-reduced-motion', isReducedMotion: input.reducedMotion };
        case 'camera-zoom':
            return { type: 'set-camera-zoom', level: input.level };
        case 'debug-overlay-settings':
            return { type: 'set-debug-overlay-settings', settings: input.settings };
        default:
            assertNever(input, 'input');
    }
}

const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/** Whether UI that should keep keyboard focus has it: an open modal, a dialog, or a text field. */
function isUiHoldingFocus(): boolean {
    if (document.querySelector('[aria-modal="true"]') !== null) {
        return true;
    }
    const active = document.activeElement;
    if (!(active instanceof HTMLElement)) {
        return false;
    }
    return active.isContentEditable || EDITABLE_TAGS.has(active.tagName) || active.closest('[role="dialog"]') !== null;
}

/**
 * The page's side of a running game. Owns everything that needs the DOM:
 * sizing the canvas and keeping it focused, registering it with the page's
 * input capture layer (whose intents it forwards), showing the cursor the
 * game asks for, and relaying state between the game and React's
 * `GameStateBridge`. Where the game itself runs is up to `connect`.
 */
export function createGamePageHost(
    canvas: HTMLCanvasElement,
    bridge: GameStateBridge,
    connect: GameConnector,
    onSceneMessage?: (message: GameSceneMessage) => void,
): GamePageHost {
    // The canvas's drawing buffer is fixed at the moment it is handed to the
    // renderer (`transferControlToOffscreen()`), so size it first. It may not
    // be laid out yet (a detached canvas reports 0x0), so fall back to its
    // attribute size - see `syncCanvasSize` for why the mount call matters.
    const initialBounds = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, Math.floor(initialBounds.width || canvas.width || window.innerWidth));
    canvas.height = Math.max(1, Math.floor(initialBounds.height || canvas.height || window.innerHeight));
    let bufferSize = { width: canvas.width, height: canvas.height };
    // The game's own cursor from the start, before its first hover report.
    canvas.style.cursor = CURSOR_CSS.default;

    // A shut-down game never answers, so anything still waiting then is refused.
    const pendingInventoryMoves = createPendingCalls<boolean>(false);
    const pendingStartCrafts = createPendingCalls<StartCraftResult>(START_CRAFT_UNAVAILABLE);

    const handleMessage = (message: GameConnectionMessage): void => {
        switch (message.type) {
            case 'scene-error':
            case 'run-models-ready':
            case 'gathering-ready':
                onSceneMessage?.(message);
                return;
            case 'ui-state':
                bridge.update(message.patch);
                return;
            case 'ui-map':
                bridge.updateMap(message.map);
                return;
            case 'cursor':
                canvas.style.cursor = CURSOR_CSS[message.mode];
                return;
            case 'sound':
                getPageAudio().handle(message.command);
                return;
            case 'inventory-move-result':
                pendingInventoryMoves.settle(message.callId, message.accepted);
                return;
            case 'start-craft-result':
                pendingStartCrafts.settle(message.callId, message.result);
                return;
            default:
                assertNever(message, 'page message');
        }
    };

    const connection = connect(canvas, handleMessage);
    let hasBegun = false;
    let isRunStarted = false;

    const resolveStartOptions = (session: GameRunSession): GameStartOptions => ({
        sessionConnection: session.sessionConnection,
        sessionToken: session.sessionToken,
        characterId: getStoredSelectedCharacterId() ?? '',
        location: {
            origin: window.location.origin,
            protocol: window.location.protocol,
            hostname: window.location.hostname,
            port: window.location.port,
        },
        debugOverlaySettings: bridge.getState().debugOverlaySettings,
        displaySettings: bridge.getDisplaySettings(),
        cameraZoomLevel: bridge.getCameraZoom().level,
    });

    const toSurfacePosition = (client: Vector2): Vector2 => {
        const rect = canvas.getBoundingClientRect();
        const scaleX = rect.width > 0 ? bufferSize.width / rect.width : 1;
        const scaleY = rect.height > 0 ? bufferSize.height / rect.height : 1;
        return { x: (client.x - rect.left) * scaleX, y: (client.y - rect.top) * scaleY };
    };
    // Gameplay input and canvas focus only once the run starts: while
    // gathering, keys belong to the page (its own bindings, the chat box).
    let detachInput: () => void = () => undefined;
    const attachInput = (): void => {
        detachInput = getPageInput().attachSurface({
            element: canvas,
            toSurfacePosition,
            sink: {
                intentStart: (intent, position, isPress) => connection.send({ type: 'intent-start', intent, position, isPress }),
                intentEnd: (intent) => connection.send({ type: 'intent-end', intent }),
                pointerMove: (position) => connection.send({ type: 'pointer-move', position }),
                moveDirection: (direction) => connection.send({ type: 'move-direction', direction }),
                zoom: (deltaY) => bridge.wheelCameraZoom(deltaY),
            },
        });
    };

    // Keyboard input goes to the game only while the canvas has focus (see
    // PageInput), so the canvas must be focusable, and focus must come back to
    // it after the player clicks the HUD on top of it - otherwise one click on
    // an action-bar slot would leave Space and the other bindings dead until
    // they clicked the world again. Deferred so a click that opens a modal
    // lets the modal take focus first.
    canvas.tabIndex = 0;
    canvas.style.outline = 'none';
    const focusCanvas = (): void => {
        if (isRunStarted && canvas.isConnected) {
            canvas.focus({ preventScroll: true });
        }
    };
    const returnFocusAfterHudClick = (event: PointerEvent): void => {
        if (!isRunStarted || event.target === canvas) {
            return;
        }
        setTimeout(() => {
            if (canvas.isConnected && document.activeElement !== canvas && !isUiHoldingFocus()) {
                focusCanvas();
            }
        }, 0);
    };
    window.addEventListener('pointerup', returnFocusAfterHudClick, true);

    const unsubscribeGameInput = bridge.subscribeGameInput((input) => connection.send(toGameCommand(input)));

    /**
     * Must also be called once by whoever attaches the canvas to the DOM (see
     * `mountGameCanvas()` in game-session-store.ts): the canvas is created
     * detached, so the initial measurement above falls back to the browser's
     * default 300x150 canvas size - 300 is truthy and wins the `||` chain.
     * Without a mount-time call the game renders at 300x150 forever, scaled up
     * by CSS into a blurry, "zoomed-in" view.
     */
    const syncCanvasSize = (): void => {
        bufferSize = measureCanvas(canvas);
        connection.send({ type: 'resize', width: bufferSize.width, height: bufferSize.height });
    };
    window.addEventListener('resize', syncCanvasSize);

    return {
        gameplayApi: {
            requestInventoryMove: (requestId, itemId, target) => pendingInventoryMoves.call((callId) => {
                connection.send({ type: 'request-inventory-move', callId, requestId, itemId, target });
            }),
            requestConsumeCrystal: (itemId): void => {
                connection.send({ type: 'request-consume-crystal', itemId });
            },
            requestDropItem: (itemId): void => {
                connection.send({ type: 'request-drop-item', itemId });
            },
            requestCancelCrystalUse: (): void => {
                connection.send({ type: 'request-cancel-crystal-use' });
            },
            requestCastAbility: (skillId): void => {
                connection.send({ type: 'request-cast-ability', skillId });
            },
            requestCancelCastAbility: (): void => {
                connection.send({ type: 'request-cancel-cast-ability' });
            },
            requestLeaveRun: (): void => {
                connection.send({ type: 'request-leave-run' });
            },
            requestFallenChoice: (choice): void => {
                connection.send({ type: 'request-fallen-choice', choice });
            },
            requestTourHold: (active): void => {
                connection.send({ type: 'request-tour-hold', isActive: active });
            },
            requestStartCraft: (slotIndex, recipeId) => pendingStartCrafts.call((callId) => {
                connection.send({ type: 'request-start-craft', callId, slotIndex, recipeId });
            }),
            requestCancelCraft: (coalescenceId): void => {
                connection.send({ type: 'request-cancel-craft', coalescenceId });
            },
        },
        beginGathering: (withGathering): void => {
            if (hasBegun) {
                return;
            }
            hasBegun = true;
            connection.begin({ kind: 'gathering', withGathering });
        },
        sendGathering: (command): void => {
            connection.sendGathering(command);
        },
        startRun: (session): void => {
            if (isRunStarted) {
                return;
            }
            isRunStarted = true;
            const options = resolveStartOptions(session);
            if (hasBegun) {
                connection.startRun(options);
            } else {
                hasBegun = true;
                connection.begin({ kind: 'run', options });
            }
            attachInput();
            focusCanvas();
        },
        get isRunStarted(): boolean {
            return isRunStarted;
        },
        syncCanvasSize,
        focus: focusCanvas,
        captureCleanRender: (scale) => (hasBegun
            ? connection.captureCleanRender(scale)
            : Promise.resolve({ ok: false, reason: 'The scene has not started yet.' })),
        dispose: (): Promise<void> => {
            window.removeEventListener('resize', syncCanvasSize);
            window.removeEventListener('pointerup', returnFocusAfterHudClick, true);
            unsubscribeGameInput();
            detachInput();
            // The game's ambience and loops end with it.
            getPageAudio().handle({ kind: 'stop-all' });
            const stopped = connection.dispose();
            pendingInventoryMoves.abandon();
            pendingStartCrafts.abandon();
            return stopped;
        },
    };
}
